// What an object *is*, re-derived from the stage it currently reads.
//
// Five ladders own an object's whole presentation -- `vines`, `rust`, `sand`,
// `relic`, and the water/lava freeze -- and between them they can change three
// things a player can see: the sprite, the Inspect text, and the name on the
// "Facing:" label. Only `relic.ts` ever changed all three, and it only got
// there by being rewritten (2026-08-09) after exactly this bug: a restored
// Withered Standard showed a pristine sprite while still reading "corroded
// past any hope of repair".
//
// The others kept the same bug in a milder form, reported 2026-08-11: a Sand
// Block heated into a slumped mass of molten glass said so when inspected and
// was still called "Sand Block"; a Metal Block eaten through with rust was
// still a "Metal Block"; a tangle of blackened, dead vines was still "Vines".
// The description moved and the title did not, which reads as the world having
// two opinions about what you are looking at -- and the one it prints in the
// larger type is the stale one.
//
// So the three move together, in one function, and a stage states all three or
// none. There is deliberately no way to express "rename but keep the old
// description": a ladder that wants a new name for a stage has to say what that
// stage looks like and reads as too, which is the whole content of "this is a
// different thing now".
import { ObjectDef } from './types';
import { SimEvents, TextureTransition } from './events';

/** Everything one stage of a ladder says about the object in it.
 *
 * `name` is optional only for a ladder that genuinely never renames -- the
 * freeze/thaw pair restoring an authored name is the one case, and it supplies
 * the name it recorded rather than omitting the field. */
export interface StagePresentation {
  name?: string;
  texture: string;
  description: string;
}

/** Applies one stage's presentation to `obj`, emitting only what actually
 * changed.
 *
 * Every field is **written back** as well as announced, not merely compared:
 * sprites are rebuilt from `obj.texture` on every room entry, and the faced
 * -target label reads `obj.name` straight off the object, so an event alone
 * would forget the stage the moment the player left and came back. Every one of
 * these ladders is also reversible, so a stage that cycles back to the
 * authored look would otherwise compare equal to a never-updated field and skip
 * re-emitting -- the bug `refreshRustTexture` documents at length, and the
 * reason it, `refreshRelicState` and this function all write rather than only
 * compare.
 *
 * `interact-changed` covers the name as well as the text: it is the event that
 * tells a renderer "what you are facing reads differently now", and the label
 * and the description are two halves of the same answer. So a rename with an
 * unchanged description still emits, which is why `renamed` is checked into the
 * second condition rather than the two being tested independently. */
export function applyStagePresentation(obj: ObjectDef, stage: StagePresentation, out: SimEvents, transition: TextureTransition = 'instant'): void {
  if (obj.texture !== stage.texture) {
    out.push({ kind: 'texture-set', objectId: obj.id, texture: stage.texture, transition });
    obj.texture = stage.texture;
  }
  const renamed = stage.name !== undefined && obj.name !== stage.name;
  if (renamed) obj.name = stage.name!;
  if (renamed || obj.interact?.text !== stage.description) {
    obj.interact = { ...obj.interact, label: obj.interact?.label ?? 'Inspect', text: stage.description };
    out.push({ kind: 'interact-changed', objectId: obj.id });
  }
}
